import React, { Component } from 'react';
import { arrayOf, bool, object, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route, withRouter } from 'react-router-dom';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { useConfiguration } from '../context/configurationContext';
import { propTypes } from '../util/types';
import * as log from '../util/log';
import { canonicalRoutePath } from '../util/routes';

import { locationChanged } from '../ducks/routing.duck';
import { manageDisableScrolling } from '../ducks/ui.duck';
import { getUserRole } from '../ducks/user.duck';

import { Modal, NamedRedirect } from '../components';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import LoadableComponentErrorBoundary from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary';

import { USER_ROLE } from '../constants/user';

/* =========================
   Authorization helpers
========================= */

const isAuthorized = props => {
  const { route, match, currentUserRole } = props;
  const { role } = route;
  const userRole = typeof role === 'function' ? role(match.params) : role;
  return !userRole || userRole === currentUserRole;
};

const canShowComponent = props => {
  const { isAuthenticated, route } = props;
  const { auth } = route;
  return (!auth || isAuthenticated) && isAuthorized(props);
};

/* =========================
   Data loading
========================= */

const callLoadData = props => {
  const { match, location, route, dispatch, logoutInProgress, config, logLoadDataCalls } = props;

  const { loadData, name } = route;

  if (typeof loadData === 'function' && canShowComponent(props) && !logoutInProgress) {
    dispatch(loadData(match.params, location.search, config, match.path))
      .then(() => {
        if (logLoadDataCalls) {
          console.log(`loadData success for ${name}`);
        }
      })
      .catch(e => {
        log.error(e, 'load-data-failed', { routeName: name });
      });
  }
};

/* =========================
   Scroll + routing
========================= */

const setPageScrollPosition = location => {
  if (!location.hash) {
    window.scrollTo(0, 0);
  } else {
    const el = document.querySelector(location.hash);
    if (el) {
      el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }
};

const handleLocationChanged = (dispatch, location, routeConfiguration) => {
  setPageScrollPosition(location);
  const path = canonicalRoutePath(routeConfiguration, location);
  dispatch(locationChanged(location, path));
};

/* =========================
   Route renderer
========================= */

class RouteComponentRenderer extends Component {
  componentDidMount() {
    const { dispatch, location, routeConfiguration } = this.props;
    callLoadData(this.props);
    handleLocationChanged(dispatch, location, routeConfiguration);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location !== this.props.location) {
      const { dispatch, location, routeConfiguration } = this.props;
      callLoadData(this.props);
      handleLocationChanged(dispatch, location, routeConfiguration);
    }
  }

  render() {
    const { route, match, location, staticContext, isAuthenticated } = this.props;

    const {
      component: RouteComponent,
      authPage = route.role === USER_ROLE.ARTIST ? 'ArtistSignupPage' : 'SignupPage',
      extraProps,
    } = route;

    const canShow = canShowComponent(this.props);
    const redirectPage = !isAuthenticated ? authPage : 'LandingPage';

    if (!canShow && staticContext) {
      staticContext.unauthorized = true;
    }

    return canShow ? (
      <LoadableComponentErrorBoundary>
        <RouteComponent params={match.params} location={location} {...extraProps} />
      </LoadableComponentErrorBoundary>
    ) : (
      <NamedRedirect
        name={redirectPage}
        state={{
          from: `${location.pathname}${location.search}${location.hash}`,
        }}
      />
    );
  }
}

RouteComponentRenderer.defaultProps = {
  staticContext: {},
};

RouteComponentRenderer.propTypes = {
  dispatch: func.isRequired,
  isAuthenticated: bool.isRequired,
  logoutInProgress: bool.isRequired,
  currentUserRole: string,
  route: propTypes.route.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  match: shape({
    params: object.isRequired,
    url: string.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  staticContext: object,
};

/* =========================
   Redux connection
========================= */

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
  logoutInProgress: state.auth.logoutInProgress,
  currentUserRole: getUserRole(state),
});

const RouteComponentContainer = connect(mapStateToProps)(RouteComponentRenderer);

/* =========================
   Routes
========================= */

const Routes = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const { location, history, onManageDisableScrolling } = props;

  const background = location?.state?.background;
  const switchLocation = background || location;

  const authRouteNames = ['LoginPage', 'SignupPage', 'ArtistSignupPage', 'ConfirmPage'];

  const toRouteComponent = route => {
    const isExact = route.exact != null ? route.exact : true;

    return (
      <Route
        key={route.name}
        path={route.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentContainer
            {...props}
            route={route}
            routeConfiguration={routeConfiguration}
            config={config}
            match={matchProps.match}
            location={matchProps.location}
            staticContext={matchProps.staticContext}
          />
        )}
      />
    );
  };

  const handleModalClose = () => {
    if (background) {
      history.replace(background);
    } else {
      history.replace('/');
    }
  };

  return (
    <>
      <Switch location={switchLocation}>
        {routeConfiguration.map(toRouteComponent)}
        <Route component={NotFoundPage} />
      </Switch>

      {background &&
        routeConfiguration
          .filter(r => authRouteNames.includes(r.name))
          .map(route => (
            <Route
              key={`modal-${route.name}`}
              path={route.path}
              exact={route.exact !== false}
              render={matchProps => (
                <Modal
                  id={`modal-${route.name}`}
                  isOpen
                  usePortal
                  onClose={handleModalClose}
                  onManageDisableScrolling={onManageDisableScrolling}
                >
                  <RouteComponentContainer
                    {...props}
                    route={route}
                    routeConfiguration={routeConfiguration}
                    config={config}
                    match={matchProps.match}
                    location={location}
                    staticContext={matchProps.staticContext}
                  />
                </Modal>
              )}
            />
          ))}
    </>
  );
};

/* =========================
   Export
========================= */

const mapDispatchToPropsRoutes = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

export default compose(
  withRouter,
  connect(
    null,
    mapDispatchToPropsRoutes
  )
)(Routes);
